import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { unifiedOrgKeys } from './useUnifiedOrganization';
import { Org } from '../types/dynamodb-tables';

/**
 * Organization Selection Management Hook
 * 
 * This hook provides intelligent organization selection functionality with:
 * - Cache-first organization lookup for performance
 * - Automatic cache invalidation on selection changes
 * - Prefetching for improved user experience
 * - Query client integration for data consistency
 * - Fallback mechanisms when cache misses occur
 * 
 * Key Features:
 * - Cache-aware district/school selection
 * - Automatic school list refresh when district changes
 * - Prefetching of organization details for faster navigation
 * - Integration with unified organization query system
 * - Performance optimization through intelligent caching
 */

// Custom hook to handle organization selection with caching and prefetching
export const useOrganizationSelection = (
  setSelectedDistrict: (org: Org) => void,
  setSelectedSchool: (org: Org) => void,
  clearSelectedDistrict: () => void,
  clearSelectedSchool: () => void,
  districts: Org[],
  schools: Org[]
) => {
  const queryClient = useQueryClient();

  const handleDistrictChange = useCallback(async (orgId: string) => {
    if (orgId === "__clear__") {
      clearSelectedDistrict();
      return;
    }
    
    // First check cache for the organization
    const cachedOrg = queryClient.getQueryData(unifiedOrgKeys.byId(orgId));
    if (cachedOrg) {
      setSelectedDistrict(cachedOrg as Org);
      clearSelectedSchool();
      queryClient.invalidateQueries({ queryKey: ['rbac-organizations', 'schools'] });
      queryClient.invalidateQueries({ queryKey: unifiedOrgKeys.rbac.schools(null) });
      return;
    }

    // Fallback to finding it in the districts list
    const selectedOrg = districts.find((district: Org) => district.orgId === orgId);
    if (selectedOrg) {
      setSelectedDistrict(selectedOrg);
      clearSelectedSchool();
      queryClient.invalidateQueries({ queryKey: ['rbac-organizations', 'schools'] });
      queryClient.invalidateQueries({ queryKey: unifiedOrgKeys.rbac.schools(null) });
      
      // Prefetch the full organization data for next time
      queryClient.prefetchQuery({
        queryKey: unifiedOrgKeys.byId(orgId),
        queryFn: async () => {
          const { organizationService } = await import('../services/api/organizationService');
          return organizationService.getOrgById(orgId);
        },
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [districts, setSelectedDistrict, clearSelectedDistrict, clearSelectedSchool, queryClient]);

  const handleSchoolChange = useCallback(async (orgId: string) => {
    if (orgId === "__clear__") {
      clearSelectedSchool();
      return;
    }
    
    // First check cache for the organization
    const cachedOrg = queryClient.getQueryData(unifiedOrgKeys.byId(orgId));
    if (cachedOrg) {
      setSelectedSchool(cachedOrg as Org);
      return;
    }

    // Always fetch the complete organization record instead of using incomplete search results
    try {
      const { organizationService } = await import('../services/api/organizationService');
      const completeSchoolData = await organizationService.getOrgById(orgId);
      
      // Cache the complete data
      queryClient.setQueryData(unifiedOrgKeys.byId(orgId), completeSchoolData);
      
      // Set the school with complete IDP fields
      setSelectedSchool(completeSchoolData);
    } catch (error) {
      console.warn('Failed to fetch complete school data, falling back to search results:', error);
      
      // Fallback to finding it in the schools list (incomplete data)
      const selectedSchoolObj = schools.find((school: Org) => school.orgId === orgId);
      if (selectedSchoolObj) {
        setSelectedSchool(selectedSchoolObj);
      }
    }
  }, [schools, setSelectedSchool, clearSelectedSchool, queryClient]);

  return {
    handleDistrictChange,
    handleSchoolChange,
  };
};

/**
 * Enhanced Organization Selection Hook with Assessment Details Redirect
 * 
 * This hook extends the basic organization selection functionality with:
 * - Automatic redirect from assessment details pages when organization changes
 * - Maintains all existing caching and performance optimizations
 * - Seamless integration with existing layout components
 */
export const useOrganizationSelectionWithRedirect = (
  setSelectedDistrict: (org: Org) => void,
  setSelectedSchool: (org: Org) => void,
  clearSelectedDistrict: () => void,
  clearSelectedSchool: () => void,
  districts: Org[],
  schools: Org[]
) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on an assessment details page
  const isAssessmentDetailsPage = location.pathname.startsWith('/assessment/details/');
  
  // Get the base organization selection handlers
  const { handleDistrictChange: baseHandleDistrictChange, handleSchoolChange: baseHandleSchoolChange } = useOrganizationSelection(
    setSelectedDistrict,
    setSelectedSchool,
    clearSelectedDistrict,
    clearSelectedSchool,
    districts,
    schools
  );

  // Enhanced district change handler with redirect logic
  const handleDistrictChange = useCallback(async (orgId: string) => {
    // Call the base handler first
    await baseHandleDistrictChange(orgId);
    
    // If we're on an assessment details page and the change was successful (not a clear operation), redirect
    if (isAssessmentDetailsPage && orgId !== "__clear__") {
      navigate('/assessment');
    }
  }, [baseHandleDistrictChange, isAssessmentDetailsPage, navigate]);

  // Enhanced school change handler with redirect logic
  const handleSchoolChange = useCallback(async (orgId: string) => {
    // Call the base handler first
    await baseHandleSchoolChange(orgId);
    
    // If we're on an assessment details page and the change was successful (not a clear operation), redirect
    if (isAssessmentDetailsPage && orgId !== "__clear__") {
      navigate('/assessment');
    }
  }, [baseHandleSchoolChange, isAssessmentDetailsPage, navigate]);

  return {
    handleDistrictChange,
    handleSchoolChange,
  };
};

/**
 * Organization Preloading Hook
 * 
 * Provides proactive data loading for better user experience:
 * - Prefetches organization data before user navigation
 * - Reduces loading times for frequently accessed organizations
 * - Integrates with query client caching strategy
 * - Optimizes performance for organization-heavy workflows
 */
export const usePreloadOrganization = (orgId: string) => {
  const queryClient = useQueryClient();
  
  const preloadOrganization = useCallback(async () => {
    if (!orgId) return;
    
    await queryClient.prefetchQuery({
      queryKey: unifiedOrgKeys.byId(orgId),
      queryFn: async () => {
        const { organizationService } = await import('../services/api/organizationService');
        return organizationService.getOrgById(orgId);
      },
      staleTime: 5 * 60 * 1000,
    });
  }, [orgId, queryClient]);

  return preloadOrganization;
};